/**
 * Rate limiting interceptor for Axios.
 *
 * Features:
 * - Handles 429 Too Many Requests responses
 * - Shows toast with countdown timer
 * - Implements exponential backoff for retries
 * - Respects Retry-After header if provided
 */

import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { toast } from '@/hooks/use-toast';

/**
 * Extended config with retry metadata
 */
interface RetryConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
  _maxRetries?: number;
  _skipRateLimitToast?: boolean;
}

/**
 * Toast result type for rate limiter
 */
interface ToastResult {
  id: string;
  dismiss: () => void;
  update: (props: { id: string; description?: string }) => void;
}

/**
 * Rate limiter state for tracking active countdown toasts and rate limit status
 */
interface RateLimiterState {
  activeToast: ToastResult | null;
  countdownInterval: ReturnType<typeof setInterval> | null;
  isRateLimited: boolean;
  rateLimitEndTime: number | null;
  listeners: Set<(isRateLimited: boolean) => void>;
}

const state: RateLimiterState = {
  activeToast: null,
  countdownInterval: null,
  isRateLimited: false,
  rateLimitEndTime: null,
  listeners: new Set(),
};

/**
 * Set rate limit status and notify listeners
 */
function setRateLimitStatus(isLimited: boolean, endTime: number | null = null): void {
  state.isRateLimited = isLimited;
  state.rateLimitEndTime = endTime;
  state.listeners.forEach((listener) => {
    listener(isLimited);
  });
}

/**
 * Check if currently rate limited.
 * @returns true if rate limited, false otherwise
 */
export function isRateLimited(): boolean {
  // Check if rate limit has expired
  if (state.isRateLimited && state.rateLimitEndTime !== null) {
    if (Date.now() >= state.rateLimitEndTime) {
      setRateLimitStatus(false);
    }
  }
  return state.isRateLimited;
}

/**
 * Get the time remaining until rate limit expires.
 * @returns milliseconds until rate limit expires, or 0 if not rate limited
 */
export function getRateLimitRemaining(): number {
  if (!state.isRateLimited || state.rateLimitEndTime === null) {
    return 0;
  }
  const remaining = state.rateLimitEndTime - Date.now();
  return Math.max(0, remaining);
}

/**
 * Subscribe to rate limit status changes.
 * @param listener - Callback function called when rate limit status changes
 * @returns Unsubscribe function
 */
export function subscribeToRateLimitStatus(listener: (isRateLimited: boolean) => void): () => void {
  state.listeners.add(listener);
  // Immediately call with current status
  listener(state.isRateLimited);
  return () => {
    state.listeners.delete(listener);
  };
}

/**
 * Configuration options for rate limit interceptor
 */
export interface RateLimitInterceptorOptions {
  /**
   * Maximum number of retries for rate-limited requests.
   * @default 3
   */
  maxRetries?: number;
  /**
   * Base delay in milliseconds for exponential backoff.
   * @default 1000
   */
  baseDelay?: number;
  /**
   * Maximum delay in milliseconds between retries.
   * @default 60000 (1 minute)
   */
  maxDelay?: number;
  /**
   * Whether to show toast notifications for rate limiting.
   * @default true
   */
  showToasts?: boolean;
  /**
   * Callback when rate limit is hit.
   */
  onRateLimited?: (retryAfter: number, retryCount: number) => void;
}

/**
 * Parse Retry-After header value.
 * Can be either a number of seconds or an HTTP date.
 *
 * @param retryAfter - The Retry-After header value
 * @returns Delay in milliseconds
 */
function parseRetryAfter(retryAfter: string | undefined): number | null {
  if (!retryAfter) {
    return null;
  }

  // Try parsing as seconds
  const seconds = parseInt(retryAfter, 10);
  if (!isNaN(seconds)) {
    return seconds * 1000;
  }

  // Try parsing as HTTP date
  const date = Date.parse(retryAfter);
  if (!isNaN(date)) {
    const delay = date - Date.now();
    return delay > 0 ? delay : null;
  }

  return null;
}

/**
 * Calculate delay using exponential backoff with jitter.
 *
 * @param retryCount - Current retry attempt (0-based)
 * @param baseDelay - Base delay in ms
 * @param maxDelay - Maximum delay in ms
 * @returns Delay in milliseconds
 */
function calculateBackoffDelay(retryCount: number, baseDelay: number, maxDelay: number): number {
  // Exponential backoff: baseDelay * 2^retryCount
  const exponentialDelay = baseDelay * Math.pow(2, retryCount);

  // Add jitter (random factor between 0.5 and 1.5)
  const jitter = 0.5 + Math.random();
  const delayWithJitter = exponentialDelay * jitter;

  // Cap at maxDelay
  return Math.min(delayWithJitter, maxDelay);
}

/**
 * Show a countdown toast for rate limiting.
 *
 * @param delayMs - Delay in milliseconds
 * @param retryCount - Current retry count
 * @param maxRetries - Maximum retries allowed
 */
function showRateLimitToast(delayMs: number, retryCount: number, maxRetries: number): void {
  // Clear any existing countdown
  if (state.countdownInterval) {
    clearInterval(state.countdownInterval);
    state.countdownInterval = null;
  }

  // Dismiss existing toast
  if (state.activeToast) {
    state.activeToast.dismiss();
    state.activeToast = null;
  }

  // Set rate limit status
  setRateLimitStatus(true, Date.now() + delayMs);

  const totalSeconds = Math.ceil(delayMs / 1000);
  let remainingSeconds = totalSeconds;

  // Create toast with countdown
  const toastResult = toast.warning({
    title: 'Too Many Requests',
    description: `Rate limited. Retrying in ${String(remainingSeconds)}s (attempt ${String(retryCount + 1)}/${String(maxRetries)})`,
    duration: 0, // Don't auto-dismiss
  });

  state.activeToast = toastResult as ToastResult;

  // Start countdown
  state.countdownInterval = setInterval(() => {
    remainingSeconds -= 1;

    if (remainingSeconds <= 0) {
      // Clear interval and dismiss toast
      if (state.countdownInterval) {
        clearInterval(state.countdownInterval);
        state.countdownInterval = null;
      }
      if (state.activeToast) {
        state.activeToast.dismiss();
        state.activeToast = null;
      }
      // Clear rate limit status
      setRateLimitStatus(false);
    } else if (state.activeToast) {
      // Update toast with new countdown
      state.activeToast.update({
        id: (toastResult as { id: string }).id,
        description: `Rate limited. Retrying in ${String(remainingSeconds)}s (attempt ${String(retryCount + 1)}/${String(maxRetries)})`,
      });
    }
  }, 1000);
}

/**
 * Show a toast when max retries exceeded.
 */
function showMaxRetriesExceededToast(): void {
  // Clear any countdown state
  if (state.countdownInterval) {
    clearInterval(state.countdownInterval);
    state.countdownInterval = null;
  }
  if (state.activeToast) {
    state.activeToast.dismiss();
    state.activeToast = null;
  }

  // Clear rate limit status
  setRateLimitStatus(false);

  toast.error({
    title: 'Request Failed',
    description: 'Too many requests. Please wait a moment and try again.',
    duration: 5000,
  });
}

/**
 * Sleep for a specified duration.
 *
 * @param ms - Duration in milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Add rate limit handling interceptor to an Axios instance.
 *
 * @param axiosInstance - The Axios instance to add the interceptor to
 * @param options - Configuration options
 * @returns Function to remove the interceptor
 *
 * @example
 * ```typescript
 * import { apiClient } from './client';
 * import { addRateLimitInterceptor } from './rateLimitInterceptor';
 *
 * // Add the interceptor
 * const removeInterceptor = addRateLimitInterceptor(apiClient, {
 *   maxRetries: 3,
 *   showToasts: true,
 * });
 *
 * // Later, if needed, remove the interceptor
 * removeInterceptor();
 * ```
 */
export function addRateLimitInterceptor(
  axiosInstance: AxiosInstance,
  options: RateLimitInterceptorOptions = {}
): () => void {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 60000,
    showToasts = true,
    onRateLimited,
  } = options;

  const interceptorId = axiosInstance.interceptors.response.use(
    // Pass through successful responses
    (response) => response,
    // Handle errors
    async (error: AxiosError) => {
      const config = error.config as RetryConfig | undefined;

      // Only handle 429 errors
      if (error.response?.status !== 429 || !config) {
        return Promise.reject(error);
      }

      // Initialize retry count
      const retryCount = config._retryCount ?? 0;
      const effectiveMaxRetries = config._maxRetries ?? maxRetries;

      // Check if we've exceeded max retries
      if (retryCount >= effectiveMaxRetries) {
        if (showToasts && !config._skipRateLimitToast) {
          showMaxRetriesExceededToast();
        }
        return Promise.reject(error);
      }

      // Determine delay from Retry-After header or exponential backoff
      const retryAfterHeader = error.response.headers['retry-after'] as string | undefined;
      const retryAfterDelay = parseRetryAfter(retryAfterHeader);
      const backoffDelay = calculateBackoffDelay(retryCount, baseDelay, maxDelay);
      const delay = retryAfterDelay ?? backoffDelay;

      // Show toast notification
      if (showToasts && !config._skipRateLimitToast) {
        showRateLimitToast(delay, retryCount, effectiveMaxRetries);
      }

      // Call callback if provided
      onRateLimited?.(delay, retryCount);

      // Wait for the delay
      await sleep(delay);

      // Increment retry count and retry the request
      config._retryCount = retryCount + 1;

      return axiosInstance.request(config);
    }
  );

  // Return function to remove the interceptor
  return () => {
    axiosInstance.interceptors.response.eject(interceptorId);

    // Clean up any active toast/countdown
    if (state.countdownInterval) {
      clearInterval(state.countdownInterval);
      state.countdownInterval = null;
    }
    if (state.activeToast) {
      state.activeToast.dismiss();
      state.activeToast = null;
    }
  };
}

/**
 * Custom error class for rate limit exceeded
 */
export class RateLimitError extends Error {
  public readonly retryAfter: number;
  public readonly originalError: AxiosError;

  constructor(message: string, retryAfter: number, originalError: AxiosError) {
    super(message);
    this.name = 'RateLimitError';
    this.retryAfter = retryAfter;
    this.originalError = originalError;
  }
}

export default addRateLimitInterceptor;
